import { useEffect, useState } from "react";
import {
  getRecordedListingContacts,
  loadRecordedListingContacts,
  subscribeToRecordedListingContacts
} from "utils/listings/recordedListingContacts";
import type { ListingContact } from "../@types/sharedSchema";

/**
 * The owner a realtor recorded on her own listing, read from the AUTHORITY
 * (`real-estate-listings`) rather than from the world-readable mirror row the
 * surface is holding. See `utils/listings/recordedListingContacts.ts` for why
 * the mirror must never carry it, and why reading the authority is also the
 * FRESHER answer rather than merely the safe one.
 *
 * Returns `undefined` while unknown, `[]` once we know nothing is recorded.
 *
 * ⚠ `enabled` must be `listingOrigin === 'webot'`. Every other origin is a market
 * ad with no listing document of ours behind it, so a read is a guaranteed miss;
 * gating here is what keeps a 200-row board to a handful of point reads.
 */
export function useRecordedListingContacts(
  listingId: string | undefined,
  enabled: boolean
): ListingContact[] | undefined {
  const [contacts, setContacts] = useState<ListingContact[] | undefined>(() =>
    enabled && listingId ? getRecordedListingContacts(listingId) : undefined
  );

  useEffect(() => {
    let cancelled = false;

    if (!enabled || !listingId) {
      setContacts(undefined);
      return () => {
        cancelled = true;
      };
    }

    // A fresh id starts from whatever the shared cache already knows about IT —
    // never from the previous id's answer. Without this reset a surface that
    // swaps listings under a mounted hook (the details dialog) shows the last
    // listing's owner until the new read lands.
    setContacts(getRecordedListingContacts(listingId));

    const sync = (): void => {
      const cached = getRecordedListingContacts(listingId);
      if (cached) {
        if (!cancelled) setContacts(cached);
        return;
      }
      // Missing or expired — (re)load. The store coalesces this into whatever
      // batch is forming, and de-dupes rows asking for the same id.
      void loadRecordedListingContacts(listingId).then((resolved) => {
        // `undefined` means the store could not answer (a failed read, or its
        // backoff window). KEEP the last good answer rather than dropping to
        // "unknown": flipping a resolved owner back to the listing agent because
        // of a refresh blip is a visibly wrong person, where a slightly stale
        // owner is still the right one.
        if (!cancelled && resolved) setContacts(resolved);
      });
    };

    sync();

    // Re-sync on flushes and on invalidation, so a save made from this very row
    // is reflected without a reload.
    const unsubscribe = subscribeToRecordedListingContacts(() => {
      if (!cancelled) sync();
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [listingId, enabled]);

  return contacts;
}

/**
 * Re-exported so a surface imports the hook and its overlay from one place.
 *
 * It LIVES in `listingSeller.ts` — beside the resolver whose input it shapes —
 * because that module is deliberately free of firebase, React, the DOM and i18n
 * so its test can be a pure unit test. Defining it here would drag
 * `firebaseConfig` into every importer, which is exactly how
 * `listingSeller.test.ts` stopped being able to run.
 */
export { withRecordedOwner } from "utils/listings/listingSeller";
